import { useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { compareThicknessAnalyses } from '../../../services/cad.service';
import {
  CADModel,
  ThicknessAnalysis,
  ThicknessComparison,
} from '../../../types/cad';
import { resolveApiErrorMessage } from '../../../utils/apiErrorMessages';

interface UseThicknessComparisonArgs {
  model: CADModel | null;
  displayedAnalysis: ThicknessAnalysis | null;
  t: TFunction;
  setError: (message: string | null) => void;
}

/** Compares the displayed run against an earlier completed baseline. */
export const useThicknessComparison = ({
  model,
  displayedAnalysis,
  t,
  setError,
}: UseThicknessComparisonArgs) => {
  const [comparisonBaseId, setComparisonBaseId] = useState('');
  const [comparison, setComparison] = useState<ThicknessComparison | null>(null);
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    setComparisonBaseId('');
    setComparison(null);
  }, [displayedAnalysis?.analysisId, model?.id]);

  const compareSelectedRuns = useCallback(async () => {
    if (!model || !displayedAnalysis || !comparisonBaseId) return;
    setComparing(true);
    try {
      const nextComparison = await compareThicknessAnalyses(
        model.id,
        comparisonBaseId,
        model.id,
        displayedAnalysis.analysisId,
      );
      setComparison(nextComparison);
      setError(null);
    } catch (comparisonError) {
      setError(
        resolveApiErrorMessage(t, comparisonError, {
          fallbackKey: 'cad.thickness.comparison.failed',
        }),
      );
    } finally {
      setComparing(false);
    }
  }, [comparisonBaseId, displayedAnalysis, model, setError, t]);

  return {
    comparisonBaseId,
    setComparisonBaseId,
    comparison,
    setComparison,
    comparing,
    compareSelectedRuns,
  };
};
